import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
// Import Components
import { SkeletonLoader } from '@geneui/components';
import BonusCalculationLogic from 'components/CampaignModal/BonusCalculationLogic';
import SectionWithTitle from 'components/common/SectionWithTitle';
// Import Hooks
import useOneSpinRates from 'hooks/useOneSpinRates';
// Import Constants
import { l } from 'constants/common';
// Import Types
import { AwardById, BonusForCampaign } from 'types';
// Import Enums
import { AwardTypesEnum } from 'enums';

interface CampaignReportBonusCalculationLogicProps {
    campaignBonus: BonusForCampaign;
    bonusInfo?: AwardById;
    isLoading: boolean;
}

const CampaignReportBonusCalculationLogic: FC<CampaignReportBonusCalculationLogicProps> = ({
    campaignBonus,
    bonusInfo,
    isLoading,
}) => {
    const { t } = useTranslation();

    const { rates: oneSpinRates, isLoading: isLoadingSpinRates } = useOneSpinRates({
        bonusType: bonusInfo?.Type,
        configJson: campaignBonus.ConfigJson,
    });

    return (
        <SectionWithTitle title={t(l.BonusCalculationLogic)} hasContentPadding={false}>
            <div className="campaign-report-bonus-calculation-logic-content">
                <div className="campaign-report-bonus-calculation-logic-scrollable-container">
                    <div className="campaign-report-bonus-calculation-key-values">
                        <BonusCalculationLogic
                            campaignBonus={campaignBonus}
                            bonusInfo={bonusInfo}
                            isLoading={isLoading}
                            isVertical={true}
                            isGrid={true}
                        />
                    </div>

                    {[AwardTypesEnum.FreeSpin, AwardTypesEnum.WonderWheelSpin].includes(bonusInfo?.Type ?? -1) && (
                        <div className="campaign-report-bonus-calculation-key-values">
                            <div className="bc-grid-layout-container">
                                <div className="title-wrapper">
                                    <p className="title-wrapper-text">{t(l.OneSpinPrice)}</p>
                                    <span className="title-wrapper-line" />
                                </div>
                                <SkeletonLoader isBusy={isLoadingSpinRates}>
                                    {oneSpinRates.map(({ Currency, Amount }, index) => {
                                        return (
                                            <div
                                                className="key-value-v bc-grid-layout-item-4"
                                                key={`${Currency} + ${index}`}
                                            >
                                                <span className="ellipsis-text kv-label">{Currency}</span>
                                                <span className="ellipsis-text kv-value kv-value-css">{Amount}</span>
                                            </div>
                                        );
                                    })}
                                </SkeletonLoader>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </SectionWithTitle>
    );
};

export default memo(CampaignReportBonusCalculationLogic);
